import { useEffect, useState, useRef } from 'react';
import * as jStat from 'jstat';

export default function StockGraph() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number>();
    const [dimensions, setDimensions] = useState({ width: 1200, height: 600 });

    // Monte Carlo Variables
    const volatility = 0.2;
    const mean = 0.05;
    const deltaTime = 1 / 252;

    // Animation Variables
    const spacing = 5;
    const stockRef = useRef(100);
    const pointsRef = useRef<number[][]>([]);

    useEffect(() => {
        const updateDimensions = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);

        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    // Reset graph when dimensions change
    useEffect(() => {
        stockRef.current = 100;
        pointsRef.current = [[0, 0.5 * dimensions.height]];
    }, [dimensions]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const draw = () => {
            ctx.clearRect(0, 0, dimensions.width, dimensions.height);

            // Reset if we hit the edge
            if (pointsRef.current.length >= (dimensions.width / spacing)) {
                stockRef.current = 100;
                pointsRef.current = [[0, 0.5 * dimensions.height]];
                return
            }

            ctx.strokeStyle = 'rgba(100, 200, 255, 0.4)';
            ctx.lineWidth = 3;
            ctx.beginPath();

            const random = jStat.normal.inv(Math.random(), mean, 1);

            // Monte Carlo Simulation
            stockRef.current = stockRef.current * (1 + mean * deltaTime + volatility * Math.sqrt(deltaTime) * random);

            pointsRef.current.push([pointsRef.current.length * spacing, (stockRef.current / 200) * dimensions.height]);

            for (let i = 0; i < pointsRef.current.length; i++) {
                if (i === 0) {
                    ctx.moveTo(pointsRef.current[i][0], pointsRef.current[i][1]);
                } else {
                    ctx.lineTo(pointsRef.current[i][0], pointsRef.current[i][1]);
                }
            }

            ctx.stroke();
        };

        const animate = () => {
            draw();
            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [dimensions]);

    return (
        <canvas
            ref={canvasRef}
            width={dimensions.width}
            height={dimensions.height}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                zIndex: 0,
                pointerEvents: "none"
            }}
        />
    )
}
